import React, { Fragment, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { isTablet } from "react-device-detect";

import { SearchRequestService } from "../services/SearchRequestService";
import * as constants from "../constants";
import withTranslation, {
  LanguageOptionType,
  WithTranslationState,
} from "./withTranslation";
import { constructResultAndInRoomSlider } from "../helpers/artWorkHelper";
import { getVisitSessionId, getHeldEmail } from "../helpers/sessionId";
import { ArtworkObject, ArtWorkRecordsResult } from "../types/payloadTypes";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { ArtworkDefault } from "./ArtworkDefault";
import { ArtworkSkeleton } from "./ArtworkSkeleton";
import { DebugOverlay } from "./DebugOverlay";
import { DEBUG_ENABLED } from "../helpers/debugEnabled";
import { track, setAnalyticsLanguage } from "../helpers/analytics";

class ArtworkErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state = { error: null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      if (DEBUG_ENABLED) {
        return (
          <div style={{
            background: "#1a0000", color: "#ff4444", padding: "16px",
            fontFamily: "monospace", fontSize: "12px", whiteSpace: "pre-wrap",
            minHeight: "50vh",
          }}>
            <div style={{ color: "#ff0", fontWeight: "bold", marginBottom: "8px" }}>
              RENDER ERROR IN ARTWORK COMPONENT
            </div>
            {this.state.error.message}
            {"\n\n"}
            {this.state.error.stack}
          </div>
        );
      }
      // Minimal user-facing fallback so visitors aren't stuck on a blank page
      return (
        <div style={{
          padding: "24px",
          textAlign: "center",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}>
          <h2>Something went wrong</h2>
          <p>We couldn't display this artwork. Please go back and try again.</p>
          <button
            onClick={() => window.history.back()}
            style={{
              marginTop: "16px",
              padding: "12px 24px",
              fontSize: "16px",
              cursor: "pointer",
            }}
          >
            Go Back
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export const ArtworkWrapperComponent: React.FC<WithTranslationState> = ({
  getSelectedLanguage,
  updateSelectedLanguage,
  langOptions,
  getTranslation,
}) => {
  // Initialize the search request services
  const sr = new SearchRequestService();
  // Component state
  const [imageId, setImageId] = useState<string>();
  const [artwork, setArtwork] = useState<ArtworkObject>();
  const [result, setResult] = useState<ArtWorkRecordsResult>();
  const [specialExhibition, setSpecialExhibition] = useState<boolean>();
  const [emailCaptured, setEmailCaptured] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(true);
  const [emailCaptureAck, setEmailCaptureAck] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageOptionType>({
    name: "English",
    code: "En",
    selected: true,
  });

  const location = useLocation();
  const { getLocalStorage } = useLocalStorage();

  const onSelectLanguage = async (selectedLanguage: LanguageOptionType) => {
    // Scroll to top when language changes. This should help re-calculate correct offsets on language change
    window.scroll({ top: 0, behavior: "smooth" });

    // Update local storage with the new set language and then update the server session
    await updateSelectedLanguage(selectedLanguage);
    setAnalyticsLanguage(selectedLanguage.code);

    let artworkInfo: ArtWorkRecordsResult;

    // Get the new language translations
    if (specialExhibition) {
      artworkInfo = await sr.getSpecialExhibitionObject(
        imageId,
        selectedLanguage.code
      );
    } else {
      artworkInfo = await sr.getArtworkInformation(
        imageId,
        selectedLanguage.code
      );
    }

    const artwork = artworkInfo
      ? constructResultAndInRoomSlider(artworkInfo, isTablet)
      : undefined;

    setResult(artworkInfo);
    setArtwork(artwork);
    setSelectedLanguage(selectedLanguage);
  };

  /** Updates state that email was captured and submits it to the server session */
  const onSubmitEmail = (email: string, callback?: (args?: any) => void) => {
    setEmailCaptured(true);
    track("recap_email_submit", {
      artwork_id: imageId,
      object_type: specialExhibition ? "exhibition" : "artwork",
    });

    // Store the email (legacy RDS path)
    sr.submitBookmarksEmail(email);

    // FOC-143: ingest to the new engine recap pipeline (session_id → email),
    // dual-written during the strangler transition. Fire-and-forget.
    const sessionId = getVisitSessionId();
    sr.submitEmailIngest(sessionId, email);

    // FOC-143 2/3: start the 24h local hold + record which session this email
    // was ingested for, so a NEW session's first scan re-submits it once.
    try {
      localStorage.setItem(constants.SNAP_USER_EMAIL_TS, Date.now().toString());
      localStorage.setItem(constants.SNAP_EMAIL_INGESTED_SID, sessionId);
    } catch {
      // localStorage unavailable — the ingest above still fired for this session
    }

    // Close the email card after 4 seconds
    setTimeout(() => {
      callback();
      setEmailCaptureAck(true);
    }, 4000);
  };

  // Revoke debug blob URLs when this page unmounts (passed from /scan via router state)
  useEffect(() => {
    return () => {
      const log = location.state?.debugLog;
      if (Array.isArray(log)) {
        log.forEach((e: { thumbUrl?: string }) => {
          if (e.thumbUrl) URL.revokeObjectURL(e.thumbUrl);
        });
      }
    };
  }, []);

  // Set state and get object data on component initialization
  useEffect(() => {
    const componentWillMount = async () => {
      try {
        let artworkInfo: ArtWorkRecordsResult;
        let imageId: string;
        const langPref = (await getSelectedLanguage())[0];

        // Check if result data is stored in location state from camera component
        if (location.state?.result) {
          imageId = location.state.result.data.records[0].id;
          artworkInfo = location.state.result;
        } else {
          // If result data is not in state, get image ID from path and get data from server
          const [_n, path, id] = location.pathname.split("/");
          imageId = id;

          if (path === "artwork") {
            artworkInfo = await sr.getArtworkInformation(imageId, langPref.code);
          } else if (path === "exhibition") {
            artworkInfo = await sr.getSpecialExhibitionObject(
              imageId,
              langPref.code
            );
          }
        }

        // If this special-exhibition object carries a redirectUrl (e.g. a QR-code
        // "sign" a visitor scanned with the Focus app, which should forward them
        // straight to an external survey/form rather than showing an interpretive
        // page), navigate there and stop. This runs before any state is set, so we
        // redirect off the loading skeleton without flashing the exhibition page.
        // Covers both entry paths: the in-app scan (result in router state) and a
        // direct /exhibition/:id load.
        const redirectUrl = artworkInfo?.data?.records?.[0]?.redirectUrl;
        if (redirectUrl) {
          window.location.replace(redirectUrl);
          return;
        }

        const artwork = constructResultAndInRoomSlider(artworkInfo, isTablet);

        setResult(artworkInfo);
        setArtwork(artwork);
        setImageId(imageId);
        setSpecialExhibition(artworkInfo.data.specialExhibition);
        track("result_view", {
          artwork_id: imageId,
          object_type: artworkInfo.data.specialExhibition
            ? "exhibition"
            : "artwork",
          entry: location.state?.result ? "scan" : "link",
        });

        // FOC-143 2/3: honor the 24h hold — an email older than 24h is expired,
        // so the email tab reappears for re-entry (Steve's post-24h UX).
        const emailRecorded = getHeldEmail() !== null;
        setEmailCaptured(emailRecorded);
        setShowEmailForm(!emailRecorded);
        setEmailCaptureAck(emailRecorded);
        setSelectedLanguage(langPref);

        // FOC-239: an instant snapshot render lacks content blocks (and anything
        // else only the server has). Fetch the full record in the background and
        // swap it in; if that fails, the snapshot page stays as it is.
        if (artworkInfo?.data?.fromSnapshot) {
          const full = await sr.getArtworkFromServer(imageId, langPref.code);
          if (!cancelled && full?.success && full?.data?.records?.length) {
            setResult(full);
            setArtwork(constructResultAndInRoomSlider(full, isTablet));
          }
        }
      } catch (err) {
        console.error("ArtworkWrapper init error:", err);
      }
    };

    let cancelled = false;
    componentWillMount();
    return () => {
      cancelled = true;
    };
  }, []);

  const debugLog = location.state?.debugLog || [];
  const scanStartTime = location.state?.scanStartTime;
  const tsttm = scanStartTime ? Date.now() - scanStartTime : undefined;
  // Phase boundaries stamped by CameraContainer — used to break TSTTM down into
  // camera setup, search, pre-nav preload, and render
  const phases = location.state?.phases;
  const renderedAt = Date.now();
  const phaseBreakdown =
    phases && scanStartTime
      ? {
          camera_ms: phases.firstSubmit - scanStartTime,
          search_ms: phases.firstMatch - phases.firstSubmit,
          preload_ms: phases.navigation - phases.firstMatch,
          render_ms: renderedAt - phases.navigation,
        }
      : undefined;

  const ready = result && artwork && imageId;

  return (
    <Fragment>
      {!ready && <ArtworkSkeleton />}
      {ready && (
        <ArtworkErrorBoundary>
          {/* Story render path retired (FOC-137): stories were abandoned
              years ago, so every result now renders ArtworkDefault — which
              makes the mobile email tab (FOC-120) universal. The standalone
              StoryPage route is a separate feature and is unaffected. */}
          <ArtworkDefault
            artwork={artwork}
            result={result}
            onSelectLanguage={onSelectLanguage}
            selectedLanguage={selectedLanguage}
            emailCaptured={emailCaptured}
            showEmailForm={showEmailForm}
            onSubmitEmail={onSubmitEmail}
            langOptions={langOptions}
            getTranslation={getTranslation}
          />
        </ArtworkErrorBoundary>
      )}
      <DebugOverlay entries={debugLog} tsttm={tsttm} phases={phaseBreakdown} />
    </Fragment>
  );
};

export const ArtworkWrapper = withTranslation(ArtworkWrapperComponent);
